import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Search,
  CheckCircle2,
  Layers,
  FileText,
  AlertCircle,
  Loader2,
  X,
  GraduationCap,
  Globe,
  Building2,
  Sparkles,
  Users,
  CreditCard,
  AlertTriangle,
} from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { B2bSponsoredCoursePreview, CourseOrgPricingTierResponse } from "@/src/api/types.gen";
import { getErrorMessage } from "../../types";

export interface LinkCourseToWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: { slug: string; name: string; member_count?: number } | null;
  assignedCourseIds: number[];
  sponsoredCourses: Array<B2bSponsoredCoursePreview>;
  onSuccess: () => Promise<void>;
}

export const LinkCourseToWorkspaceModal: React.FC<LinkCourseToWorkspaceModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  assignedCourseIds,
  sponsoredCourses,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [selectedCourseId, setSelectedCourseId] = useState<number | "">("");
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "public" | "exclusive">("all");
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedCourseId("");
    setSelectedTierId(null);
    setSearchQuery("");
    setActiveFilter("all");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const executeAssign = async (courseIdToAssign: number, tierId?: number | null) => {
    if (!slug || !workspace || !courseIdToAssign) return;

    try {
      setLinking(true);
      setError(null);
      const { data, error: apiError } = await api.linkCourseToWorkspaceApiV1B2bSlugWorkspacesWsSlugCoursesLinkPost({
        path: { slug, ws_slug: workspace.slug },
        body: {
          course_id: courseIdToAssign,
          pricing_tier_id: tierId ?? undefined,
        },
      });

      if (data) {
        handleClose();
        await onSuccess();
      } else {
        setError(getErrorMessage(apiError, "Failed to assign course to workspace"));
      }
    } catch (err) {
      console.error("Error linking course:", err);
      setError("Network error occurred while assigning course");
    } finally {
      setLinking(false);
    }
  };

  const assignedSet = useMemo(() => new Set(assignedCourseIds), [assignedCourseIds]);

  const availableCourses = useMemo(
    () => sponsoredCourses.filter((c) => !assignedSet.has(c.id)),
    [sponsoredCourses, assignedSet]
  );

  const hasBothVisibilityTypes = useMemo(() => {
    const hasPublic = availableCourses.some((c) => c.is_public !== false);
    const hasExclusive = availableCourses.some((c) => c.is_public === false);
    return hasPublic && hasExclusive;
  }, [availableCourses]);

  const filteredCourses = useMemo(() => {
    let list = availableCourses;

    // Visibility filter
    if (activeFilter === "public") {
      list = list.filter((c) => c.is_public !== false);
    } else if (activeFilter === "exclusive") {
      list = list.filter((c) => c.is_public === false);
    }

    // Search query filter
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q))
    );
  }, [availableCourses, activeFilter, searchQuery]);

  const selectedCourse = useMemo(
    () => availableCourses.find((c) => c.id === selectedCourseId),
    [availableCourses, selectedCourseId]
  );

  const memberCount = workspace?.member_count ?? 0;

  const activeTiers = useMemo(() => {
    return (selectedCourse?.org_pricing_tiers || []).filter((t) => t.is_active);
  }, [selectedCourse]);

  const recommendedTier = useMemo(() => {
    if (activeTiers.length === 0) return null;

    // Sort active tiers ascending by min_seats, then max_seats
    const sorted = [...activeTiers].sort((a, b) => {
      if (a.min_seats !== b.min_seats) return a.min_seats - b.min_seats;
      return (a.max_seats ?? Infinity) - (b.max_seats ?? Infinity);
    });

    if (memberCount <= 0) {
      return sorted[0];
    }

    // 1. Look for tier where memberCount is within [min_seats, max_seats ?? Infinity]
    const exactRangeTier = sorted.find(
      (t) => memberCount >= t.min_seats && memberCount <= (t.max_seats ?? Infinity)
    );
    if (exactRangeTier) return exactRangeTier;

    // 2. If not in exact range, find smallest tier that can cover memberCount
    const coveringTier = sorted.find((t) => (t.max_seats ?? Infinity) >= memberCount);
    if (coveringTier) return coveringTier;

    // 3. If memberCount exceeds all tiers, pick the largest capacity tier
    return sorted[sorted.length - 1];
  }, [activeTiers, memberCount]);

  const selectedTier = useMemo(() => {
    return activeTiers.find((t) => t.id === selectedTierId) ?? null;
  }, [activeTiers, selectedTierId]);

  const isTierPurchased = (tierId: number) => {
    return selectedCourse?.purchased_tier_ids?.includes(tierId) || false;
  };
  const selectedTierPurchased = selectedTier ? isTierPurchased(selectedTier.id) : false;

  useEffect(() => {
    if (recommendedTier) {
      setSelectedTierId(recommendedTier.id);
    } else if (activeTiers.length > 0) {
      setSelectedTierId(activeTiers[0].id);
    } else {
      setSelectedTierId(null);
    }
  }, [selectedCourseId, recommendedTier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof selectedCourseId !== "number") return;

    if (activeTiers.length > 0 && !selectedTier) {
      setError("Please select an employee seat tier for this course.");
      return;
    }

    if (selectedTier && selectedTier.price > 0 && !selectedTierPurchased) {
      handleClose();
      navigate(
        `/checkout/tier/${selectedTier.id}?org=${slug}&ws=${workspace?.slug}&course=${selectedCourseId}`
      );
      return;
    }

    await executeAssign(selectedCourseId, selectedTier?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Assign Course to ${workspace?.name || "Workspace"}`}
      subtitle="Select a course from your platform and enterprise catalog to enable for this workspace's members."
      icon={
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 ring-1 ring-indigo-100 text-indigo-600">
          <BookOpen className="h-4 w-4" />
        </div>
      }
      maxWidth="max-w-2xl"
    >
      {error && (
        <div className="mb-4 flex items-center gap-2.5 rounded-2xl border border-rose-200 bg-rose-50/80 p-3.5 text-xs font-semibold text-rose-800 animate-in fade-in duration-200">
          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
          <span>{error}</span>
        </div>
      )}

      {availableCourses.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
            <GraduationCap className="h-7 w-7" />
          </div>
          <h4 className="text-sm font-bold text-slate-800">All courses assigned</h4>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
            All available platform and organization courses are already assigned to{" "}
            <span className="font-semibold text-slate-700">{workspace?.name || "this workspace"}</span>.
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-slate-100 px-5 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 active:scale-95"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Search bar & quick filters */}
          <div className="space-y-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search available courses by title or topic…"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/70 py-2.5 pl-10 pr-10 text-xs font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {hasBothVisibilityTypes && (
              <div className="flex items-center gap-1.5 rounded-xl bg-slate-100/70 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setActiveFilter("all")}
                  className={`flex-1 rounded-lg py-1 text-center font-semibold transition ${
                    activeFilter === "all"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  All ({availableCourses.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveFilter("public")}
                  className={`flex-1 rounded-lg py-1 text-center font-semibold transition ${
                    activeFilter === "public"
                      ? "bg-white text-emerald-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Public Catalog
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveFilter("exclusive")}
                  className={`flex-1 rounded-lg py-1 text-center font-semibold transition ${
                    activeFilter === "exclusive"
                      ? "bg-white text-amber-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    Enterprise Exclusive
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-0.5 text-[11px] font-semibold">
            <span className="uppercase tracking-wider text-slate-400">
              {filteredCourses.length} course{filteredCourses.length === 1 ? "" : "s"} available
            </span>
            {selectedCourse ? (
              <span className="inline-flex items-center gap-1 text-indigo-600">
                <Sparkles className="h-3 w-3" />
                1 selected (double-click card to quick-assign)
              </span>
            ) : (
              <span className="text-slate-400">Click a course card to select</span>
            )}
          </div>

          {/* Course card list */}
          <div className="max-h-[340px] space-y-2.5 overflow-y-auto pr-1">
            {filteredCourses.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-xs text-slate-500">
                  {searchQuery
                    ? `No courses match "${searchQuery}"`
                    : "No courses found under this category"}
                </p>
                {(searchQuery || activeFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setActiveFilter("all");
                    }}
                    className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            ) : (
              filteredCourses.map((c) => {
                const isSelected = selectedCourseId === c.id;
                const isPublic = c.is_public !== false;

                return (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCourseId(c.id)}
                    onDoubleClick={() => executeAssign(c.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedCourseId(c.id);
                      }
                    }}
                    className={`group relative flex cursor-pointer items-center gap-3.5 rounded-2xl border p-3.5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                      isSelected
                        ? "border-indigo-600 bg-indigo-50/50 shadow-sm ring-1 ring-indigo-600/30"
                        : "border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50/70"
                    }`}
                  >
                    {/* Thumbnail or fallback badge */}
                    <div className="relative flex h-11 w-11 min-h-[44px] min-w-[44px] max-h-[44px] max-w-[44px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-br from-indigo-50 via-indigo-100/40 to-violet-100 text-indigo-600 shadow-xs">
                      {c.cover_image_url ? (
                        <img
                          src={c.cover_image_url}
                          alt={c.title}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/images/courses/python-ai.png";
                          }}
                          className="h-full w-full object-cover object-center block"
                        />
                      ) : (
                        <GraduationCap className="h-5 w-5 text-indigo-600" />
                      )}
                    </div>

                    {/* Course details */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="truncate text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition">
                          {c.title}
                        </h4>

                        {/* Public vs Enterprise Badge */}
                        {isPublic ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                            <Globe className="h-2.5 w-2.5" />
                            Public Catalog
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                            <Building2 className="h-2.5 w-2.5" />
                            Exclusive
                          </span>
                        )}
                      </div>

                      {c.description && (
                        <p className="mt-1 line-clamp-1 text-[11px] text-slate-500 leading-relaxed">
                          {c.description}
                        </p>
                      )}

                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                          <Layers className="h-3 w-3 text-slate-400" />
                          {c.total_modules ?? 0} module{(c.total_modules ?? 0) === 1 ? "" : "s"}
                        </span>
                        <span>•</span>
                        <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                          <FileText className="h-3 w-3 text-slate-400" />
                          {c.total_lessons ?? 0} lesson{(c.total_lessons ?? 0) === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>

                    {/* Radio/Check button */}
                    <div className="shrink-0 pr-1">
                      <div
                        className={`flex h-5 w-5 items-center justify-center rounded-full transition-all ${
                          isSelected
                            ? "bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-200"
                            : "border-2 border-slate-300 group-hover:border-indigo-400"
                        }`}
                      >
                        {isSelected && <CheckCircle2 className="h-3.5 w-3.5" />}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Employee Seat Tier selection if selected course has pricing tiers */}
          {selectedCourse && (
            <div className="pt-1">
              {activeTiers.length > 0 ? (
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 animate-in fade-in duration-200">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-indigo-600" />
                        Select Employee Seat Tier
                      </h4>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Workspace headcount: <span className="font-semibold text-slate-800">{memberCount} member{memberCount === 1 ? "" : "s"}</span>.
                        {recommendedTier ? " We auto-selected the recommended tier for your team size." : " Choose team seat capacity."}
                      </p>
                    </div>
                    {selectedTierId !== recommendedTier?.id && recommendedTier && (
                      <button
                        type="button"
                        onClick={() => setSelectedTierId(recommendedTier.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100 transition"
                      >
                        <Sparkles className="h-3 w-3" />
                        Reset to Recommended
                      </button>
                    )}
                  </div>

                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {activeTiers.map((tier) => {
                      const isTierSelected = selectedTierId === tier.id;
                      const isPurchased = isTierPurchased(tier.id);
                      const isFree = tier.price === 0;
                      const isRecommended = tier.id === recommendedTier?.id;
                      const isUnderCapacity = typeof tier.max_seats === "number" && tier.max_seats < memberCount;
                      const deficit = isUnderCapacity ? memberCount - (tier.max_seats ?? 0) : 0;

                      return (
                        <div
                          key={tier.id}
                          onClick={() => setSelectedTierId(tier.id)}
                          className={`cursor-pointer rounded-xl border p-3 transition-all relative ${
                            isTierSelected
                              ? isUnderCapacity
                                ? "border-amber-500 bg-amber-50/30 shadow-xs ring-1 ring-amber-500/30"
                                : "border-indigo-600 bg-white shadow-xs ring-1 ring-indigo-600/30"
                              : isUnderCapacity
                              ? "border-amber-200/80 bg-white/70 hover:border-amber-300"
                              : "border-slate-200 bg-white/80 hover:border-indigo-200"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-xs font-bold text-slate-900 truncate">{tier.name}</p>
                                {isPurchased ? (
                                  <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                    <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
                                    Purchased
                                  </span>
                                ) : isRecommended ? (
                                  <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                    <Sparkles className="h-2.5 w-2.5 text-emerald-600" />
                                    Recommended
                                  </span>
                                ) : null}
                                {isUnderCapacity && (
                                  <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                                    <AlertTriangle className="h-2.5 w-2.5 text-amber-600" />
                                    {deficit} seats short
                                  </span>
                                )}
                              </div>
                              <p className="mt-0.5 text-[11px] text-slate-500">
                                {tier.min_seats} – {tier.max_seats ? `${tier.max_seats} seats` : "Unlimited seats"}
                              </p>
                              {isUnderCapacity && (
                                <p className="mt-0.5 text-[10px] text-amber-700 font-medium">
                                  Workspace has {memberCount} members
                                </p>
                              )}
                            </div>
                            <div
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-all mt-0.5 ${
                                isTierSelected
                                  ? isUnderCapacity
                                    ? "bg-amber-600 text-white"
                                    : "bg-indigo-600 text-white"
                                  : "border-2 border-slate-300"
                              }`}
                            >
                              {isTierSelected && <CheckCircle2 className="h-3 w-3" />}
                            </div>
                          </div>
                          <div className="mt-2.5 flex items-baseline justify-between border-t border-slate-100 pt-2 text-xs">
                            <span className="font-extrabold text-slate-900">
                              {isPurchased ? (
                                <span className="inline-flex items-center gap-1.5 text-emerald-700">
                                  <span className="line-through text-slate-400 font-normal">
                                    {tier.currency === "USD" ? "$" : `${tier.currency} `}{tier.price.toFixed(2)}
                                  </span>
                                  <span>Included</span>
                                </span>
                              ) : isFree ? (
                                "Free"
                              ) : (
                                `${tier.currency === "USD" ? "$" : `${tier.currency} `}${tier.price.toFixed(2)}`
                              )}
                            </span>
                            <span className="text-[10px] text-slate-400 capitalize">
                              / {tier.billing_interval.replace("_", " ")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Guardrail Warning Banner when an under-capacity tier is selected */}
                  {selectedTier && typeof selectedTier.max_seats === "number" && selectedTier.max_seats < memberCount && (
                    <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/90 p-3 text-xs text-amber-900 animate-in fade-in duration-200">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-bold">Headcount Guardrail Notice</p>
                        <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                          This workspace currently has <span className="font-semibold">{memberCount} members</span>, but &ldquo;{selectedTier.name}&rdquo; provides a maximum of <span className="font-semibold">{selectedTier.max_seats} seats</span>.
                          {" "}At least <span className="font-semibold">{memberCount - selectedTier.max_seats} member(s)</span> in this workspace will not receive course access until you switch to a larger tier or upgrade seats.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3.5 py-2.5 text-xs text-emerald-800 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>Platform Catalog Course · Unlimited employee access included with this workspace.</span>
                </div>
              )}
            </div>
          )}

          {/* Action footer */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="text-[11px] text-slate-500">
              {selectedCourse ? (
                <span>
                  Ready to assign: <span className="font-semibold text-slate-800">&ldquo;{selectedCourse.title}&rdquo;</span>
                  {selectedTier && selectedTier.price > 0 && (
                    selectedTierPurchased ? (
                      <span className="ml-1 text-emerald-600 font-bold">
                        · Included (Already Purchased)
                      </span>
                    ) : (
                      <span className="ml-1 text-indigo-600 font-bold">
                        · {selectedTier.currency === "USD" ? "$" : `${selectedTier.currency} `}{selectedTier.price.toFixed(2)}
                      </span>
                    )
                  )}
                </span>
              ) : (
                <span>Select a course to continue</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={linking}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={linking || !selectedCourseId || (activeTiers.length > 0 && !selectedTier)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {linking ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Assigning…</span>
                  </>
                ) : selectedTier && selectedTier.price > 0 && !selectedTierPurchased ? (
                  <span className="inline-flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    Proceed to Payment ({selectedTier.currency === "USD" ? "$" : `${selectedTier.currency} `}{selectedTier.price.toFixed(2)})
                  </span>
                ) : (
                  <span>Assign to Workspace</span>
                )}
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

